import { useState } from "react";
import PageMeta from "@/components/PageMeta";

// Conversion factors
const WEIGHT = { g: 1, kg: 1000, oz: 28.3495, lb: 453.592 };
const VOLUME = { ml: 1, l: 1000, tsp: 4.92892, tbsp: 14.7868, cup_us: 236.588, cup_uk: 284.131, floz_us: 29.5735, floz_uk: 28.4131, pint_us: 473.176, pint_uk: 568.261 };
const LENGTH = { mm: 1, cm: 10, m: 1000, in: 25.4, ft: 304.8 };

function convert(value, from, to, table) {
  const v = parseFloat(value); if (isNaN(v)) return "";
  return ((v * table[from]) / table[to]).toFixed(3).replace(/\.?0+$/, "");
}

function tempConvert(value, from, to) {
  const v = parseFloat(value); if (isNaN(v)) return "";
  let c;
  if (from === "C") c = v;
  else if (from === "F") c = (v - 32) * 5 / 9;
  else if (from === "Gas") c = v === 0 ? 110 : 135 + (v - 1) * 14;
  if (to === "C") return c.toFixed(1);
  if (to === "F") return (c * 9 / 5 + 32).toFixed(1);
  if (to === "Gas") return Math.max(0, Math.round((c - 121) / 14 + 1));
}

function Row({ title, units, mode = "linear", table }) {
  const first = Object.keys(units)[0];
  const second = Object.keys(units)[1] || first;
  const [from, setFrom] = useState(first);
  const [to, setTo] = useState(second);
  const [val, setVal] = useState("");
  const result = mode === "temp" ? tempConvert(val, from, to) : convert(val, from, to, table);
  return (
    <div className="card-recipe">
      <h3 className="font-display font-extrabold text-2xl text-rf-ink mb-5">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div className="md:col-span-2">
          <label className="text-xs font-bold text-rf-ink-soft uppercase tracking-wider">From</label>
          <input data-testid={`conv-${title.toLowerCase()}-from-val`} value={val} onChange={(e) => setVal(e.target.value)} type="number" className="input-rf mt-1" />
          <select data-testid={`conv-${title.toLowerCase()}-from-unit`} value={from} onChange={(e) => setFrom(e.target.value)} className="input-rf mt-2">
            {Object.keys(units).map((u) => <option key={u} value={u}>{units[u]}</option>)}
          </select>
        </div>
        <div className="text-center text-rf-orange font-display font-black text-3xl">→</div>
        <div className="md:col-span-2">
          <label className="text-xs font-bold text-rf-ink-soft uppercase tracking-wider">To</label>
          <input data-testid={`conv-${title.toLowerCase()}-result`} readOnly value={result} className="input-rf mt-1 bg-rf-cream-alt font-display font-extrabold text-lg" />
          <select data-testid={`conv-${title.toLowerCase()}-to-unit`} value={to} onChange={(e) => setTo(e.target.value)} className="input-rf mt-2">
            {Object.keys(units).map((u) => <option key={u} value={u}>{units[u]}</option>)}
          </select>
        </div>
      </div>
    </div>
  );
}

export default function Conversions() {
  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-20">
      <PageMeta
        title="Recipe conversions — metric, imperial, US, gas mark"
        description="Instant kitchen conversions for weight, volume, temperature, gas marks and length. The cheat sheet for cooks following recipes from anywhere in the world."
        canonical="/conversions"
        keywords="kitchen conversions, metric to imperial cooking, cups to grams converter, ounces to grams, ml to cups, gas mark to celsius, gas mark to fahrenheit, oven temperature conversion, recipe conversion calculator, UK US cooking measurements, tablespoons to millilitres, pints to litres"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Recipe conversions — metric, imperial, US, gas mark",
          url: "https://recipefiler.com/conversions",
          description: "Instant kitchen conversions for weight, volume, temperature, gas marks and length between metric, imperial and US units.",
          inLanguage: "en-GB",
          isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
        }}
      />
      <p className="overline mb-4">Conversions</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]">Metric, Imperial,<br />US — instantly.</h1>
      <p className="text-rf-ink-soft mb-12 max-w-2xl">Weights, liquid volume, temperature, gas marks and length — all in one panel.</p>

      <div className="grid lg:grid-cols-2 gap-6">
        <Row
          title="Weight"
          units={{ g: "Grams (g)", kg: "Kilograms (kg)", oz: "Ounces (oz)", lb: "Pounds (lb)" }}
          table={WEIGHT}
        />
        <Row
          title="Volume"
          units={{ ml: "Millilitres (ml)", l: "Litres (L)", tsp: "Teaspoon (tsp)", tbsp: "Tablespoon (tbsp)", cup_us: "Cup (US)", cup_uk: "Cup (UK)", floz_us: "Fl oz (US)", floz_uk: "Fl oz (UK)", pint_us: "Pint (US)", pint_uk: "Pint (UK)" }}
          table={VOLUME}
        />
        <Row
          title="Temperature"
          mode="temp"
          units={{ C: "Celsius (°C)", F: "Fahrenheit (°F)", Gas: "UK Gas Mark" }}
        />
        <Row
          title="Length"
          units={{ mm: "Millimetres (mm)", cm: "Centimetres (cm)", m: "Metres (m)", in: "Inches (in)", ft: "Feet (ft)" }}
          table={LENGTH}
        />
      </div>
    </div>
  );
}
